import React from 'react';
import { theme } from '../styles/theme';

// Netherlands App Store prices and copy for homepage presentation only.
// These records never create or modify subscriptions or payment configuration.
const EUR_DISPLAY_PRICES = {
  standard: { monthly: 999, yearly: 9999 },
  pro: { monthly: 1499, yearly: 17999 },
};
const APP_STORE_URL = 'https://apps.apple.com/app/id6758546772';
const PLANS = [
  {
    id: 'standard',
    name: 'Standard',
    description: 'Your matchday essentials, in 4K.',
    designs: 6,
    tokens: { monthly: 1500, yearly: 18000 },
    features: ['Access to standard templates', '4K design generation'],
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'More tokens. Pro templates. Matchday extras.',
    designs: 15,
    tokens: { monthly: 3000, yearly: 36000 },
    features: ['Access to Pro templates', '4K design generation', 'Matchday notifications & designs'],
  },
] as const;
const formatEur = (cents: number) => `€${(cents / 100).toFixed(2)}`;

/** Self-contained App Store pricing presentation; independent of checkout. */
export const Pricing: React.FC = () => {
  const [billingPeriod, setBillingPeriod] = React.useState<'monthly' | 'yearly'>('monthly');
  const isYearly = billingPeriod === 'yearly';

  return (
    <section id="pricing" aria-labelledby="pricing-heading">
      <div className="pricing-shell">
        <header className="pricing-heading">
          <h2 id="pricing-heading">Simple Pricing</h2>
          <p>Choose your plan. Create your matchday look.</p>
        </header>

        <div className="pricing-selector" role="group" aria-label="Billing period">
          {(['monthly', 'yearly'] as const).map((period) => (
            <button
              key={period}
              type="button"
              aria-pressed={billingPeriod === period}
              onClick={() => setBillingPeriod(period)}
            >
              {period === 'monthly' ? 'Monthly' : 'Yearly'}
            </button>
          ))}
        </div>

        <div className="pricing-cards-grid">
          {PLANS.map((plan) => {
            const prices = EUR_DISPLAY_PRICES[plan.id];
            const savings = Math.round((1 - prices.yearly / (prices.monthly * 12)) * 100);
            return (
              <article className={`pricing-card pricing-card-${plan.id}`} key={plan.id} aria-labelledby={`pricing-${plan.id}-name`}>
                <div className="pricing-plan-header">
                  <div className="pricing-plan-title">
                    <img src="/images/logo_color.png" alt="" aria-hidden="true" width="315" height="200" />
                    <h3 id={`pricing-${plan.id}-name`}>{plan.name}</h3>
                  </div>
                  <p>{plan.description}</p>
                </div>

                <div className="pricing-price-block">
                  <div className="pricing-amount">
                    <strong>{formatEur(prices[billingPeriod])}</strong>
                    <span>/{isYearly ? 'year' : 'month'}</span>
                  </div>
                  <div className="pricing-billing-details" style={{ visibility: isYearly ? 'visible' : 'hidden' }} aria-hidden={!isYearly}>
                      <p className="pricing-equivalent">Equivalent to {formatEur(Math.round(prices.yearly / 12))}/month</p>
                      {plan.id === 'standard' && savings > 0 && <span className="pricing-saving">Save {savings}%</span>}
                  </div>
                </div>

                <div className="pricing-design-benefit">
                  <svg className="pricing-accent-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="m16.9 2.6 4.5 4.5-12 12-4.5-4.5 12-12ZM3.8 15.9l4.3 4.3-6 1.7 1.7-6ZM18.3 1.2a2 2 0 0 1 2.8 0l1.7 1.7a2 2 0 0 1 0 2.8l-.3.3-4.5-4.5.3-.3Z" />
                  </svg>
                  <div>
                    <h4 className="pricing-benefit-heading">
                      <span style={{ visibility: isYearly ? 'hidden' : 'visible' }} aria-hidden={isYearly}>{`At least ${plan.designs} fully polished 4K designs`}</span>
                      <span style={{ visibility: isYearly ? 'visible' : 'hidden' }} aria-hidden={!isYearly}>Fully polished 4K designs</span>
                    </h4>
                    <p className="pricing-token-explanation">
                      {isYearly
                        ? `${plan.tokens.yearly.toLocaleString('en-US')} tokens included per year.`
                        : `With your included ${plan.tokens.monthly.toLocaleString('en-US')} monthly tokens.`}
                    </p>
                    <p>Created with our professional designers’ styles.</p>
                  </div>
                </div>

                <ul className="pricing-features">
                  {plan.features.map((feature) => (
                    <li key={feature}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" />
                        <path d="m7 12 3 3 7-7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <a className="pricing-download" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                  Get the app
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                    <path d="M4 12h16m-7-7 7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </a>
              </article>
            );
          })}
        </div>

        <p className="pricing-note">Prices shown in EUR. Check the App Store or Google Play for the latest local prices and available offers. Promotions may not be reflected on this website.</p>
      </div>
      <style>{`
        #pricing {
          position: relative;
          isolation: isolate;
          padding: 32px 24px 24px;
          scroll-margin-top: 104px;
          background: #000;
          color: #fff;
          font-family: ${theme.fonts.primary};
        }
        #pricing::before {
          content: '';
          position: absolute;
          inset: 0;
          z-index: -1;
          pointer-events: none;
          background: radial-gradient(ellipse at 20% 100%, rgba(255, 39, 0, .16), transparent 45%),
                      radial-gradient(ellipse at 90% 100%, rgba(255, 0, 168, .1), transparent 45%);
        }
        #pricing .pricing-shell { max-width: 1200px; margin: 0 auto; min-width: 0; }
        #pricing .pricing-heading { text-align: center; margin-bottom: 24px; }
        #pricing .pricing-heading h2 { font-size: 36px; font-weight: 750; line-height: 1.1; letter-spacing: -.035em; margin: 0 0 8px; }
        #pricing .pricing-heading p { color: #aab0ba; font-size: 16px; line-height: 1.4; margin: 0; }
        #pricing .pricing-selector { display: flex; width: 320px; max-width: 100%; margin: 0 auto 24px; padding: 3px; border: 1px solid #55585e; border-radius: 26px; background: #101113; box-sizing: border-box; }
        #pricing .pricing-selector button { flex: 1; min-width: 0; min-height: 44px; padding: 8px 16px; border: 0; border-radius: 22px; background: transparent; color: #b6bbc5; font: inherit; font-size: 16px; cursor: pointer; transition: background-color .2s ease, color .2s ease; }
        #pricing .pricing-selector button[aria-pressed="true"] { background: ${theme.colors.gradientPrimary}; color: #fff; font-weight: 700; text-decoration: underline; text-underline-offset: 4px; }
        #pricing .pricing-cards-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 24px; align-items: stretch; }
        #pricing .pricing-card { display: flex; flex-direction: column; min-width: 0; padding: 24px; border: 2px solid #747a80; border-radius: 20px; background: linear-gradient(145deg, #0b0c0d, #08090a); }
        #pricing .pricing-card-pro { border-color: transparent; background: linear-gradient(145deg, #0b0c0d, #08090a) padding-box, ${theme.colors.gradientPrimary} border-box; }
        #pricing .pricing-plan-title { display: flex; align-items: center; gap: 12px; }
        #pricing .pricing-plan-title img { width: 56px; height: auto; flex-shrink: 0; }
        #pricing .pricing-plan-title h3 { font-size: 20px; line-height: 1.15; font-weight: 750; letter-spacing: -.025em; margin: 0; }
        #pricing .pricing-plan-header > p { margin: 8px 0 16px; font-size: 16px; color: #aab0ba; line-height: 1.4; }
        #pricing .pricing-price-block { padding-bottom: 16px; margin-bottom: 16px; border-bottom: 1px solid #303236; }
        #pricing .pricing-amount { display: flex; align-items: baseline; flex-wrap: wrap; gap: 0 10px; }
        #pricing .pricing-amount strong { font-size: 56px; line-height: 1.05; font-weight: 800; letter-spacing: -.045em; }
        #pricing .pricing-amount > span { font-size: 16px; color: #aab0ba; }
        #pricing .pricing-equivalent { color: #b6bbc5; font-size: 16px; margin: 8px 0 0; line-height: 1.4; }
        #pricing .pricing-saving { display: inline-block; color: #fff; background: #292026; font-size: 16px; font-weight: 600; border-radius: 6px; padding: 4px 8px; margin-top: 8px; }
        #pricing .pricing-design-benefit { display: flex; align-items: flex-start; gap: 10px; }
        #pricing .pricing-accent-icon { flex: 0 0 25px; width: 25px; height: 29px; color: #ff0068; }
        #pricing .pricing-card-pro .pricing-accent-icon { color: #f000a0; }
        #pricing .pricing-design-benefit h4 { font-size: 20px; font-weight: 700; line-height: 1.25; margin: 0 0 8px; letter-spacing: -.015em; }
        #pricing .pricing-benefit-heading { display: grid; }
        #pricing .pricing-benefit-heading > span { grid-area: 1 / 1; }
        #pricing .pricing-design-benefit p { font-size: 16px; line-height: 1.4; color: #b6bbc5; margin: 0; }
        #pricing .pricing-design-benefit .pricing-token-explanation { font-size: 16px; font-weight: 500; color: #dddfe3; margin: 0 0 8px; }
        #pricing .pricing-features { display: flex; flex-direction: column; gap: 8px; list-style: none; padding: 0; margin: 16px 0; }
        #pricing .pricing-features li { display: flex; align-items: flex-start; gap: 12px; color: #dddfe3; font-size: 16px; line-height: 1.4; }
        #pricing .pricing-features svg { flex: 0 0 24px; width: 24px; height: 24px; }
        #pricing .pricing-download { margin-top: auto; display: flex; align-items: center; justify-content: center; gap: 12px; padding: 12px 16px; min-height: 44px; box-sizing: border-box; border: 1px solid #b0b7c0; border-radius: 12px; color: #fff; text-decoration: none; font-size: 16px; font-weight: 600; line-height: 1.3; transition: background-color .2s ease, filter .2s ease; }
        #pricing .pricing-card-pro .pricing-download { background: ${theme.colors.gradientPrimary}; border-color: transparent; }
        #pricing .pricing-download svg { width: 25px; height: 25px; flex-shrink: 0; }
        #pricing .pricing-download:hover { background-color: #202124; filter: brightness(1.12); }
        #pricing .pricing-selector button:focus-visible, #pricing .pricing-download:focus-visible { outline: 3px solid #fff; outline-offset: 4px; }
        #pricing .pricing-note { max-width: 760px; margin: 16px auto 0; color: #b6bbc5; font-size: 16px; line-height: 1.4; text-align: center; }
        @supports (grid-template-rows: subgrid) {
          @media (min-width: 801px) {
            #pricing .pricing-cards-grid { row-gap: 0; }
            #pricing .pricing-card { display: grid; grid-template-rows: subgrid; grid-row: span 5; row-gap: 0; }
          }
        }
        @media (min-width: 801px) and (max-width: 1100px) {
          #pricing .pricing-card { padding: 24px; }
          #pricing .pricing-plan-title img { width: 54px; }
        }
        @media (max-width: 800px) {
          #pricing { padding: 24px 20px; scroll-margin-top: 88px; }
          #pricing .pricing-cards-grid { grid-template-columns: minmax(0, 1fr); max-width: 560px; margin: 0 auto; gap: 20px; }
          #pricing .pricing-card { padding: 20px; }
          #pricing .pricing-heading h2 { font-size: 24px; }
          #pricing .pricing-plan-header > p { margin-bottom: 16px; }
          #pricing .pricing-plan-title img { width: 54px; }
          #pricing .pricing-amount strong { font-size: 36px; }
          #pricing .pricing-amount > span { font-size: 16px; }
          #pricing .pricing-design-benefit h4 { font-size: 20px; }
          #pricing .pricing-features { margin-bottom: 16px; }
          #pricing .pricing-features li { font-size: 16px; }
          #pricing .pricing-download { font-size: 16px; }
          #pricing .pricing-note { font-size: 16px; }
        }
        @media (max-width: 380px) {
          #pricing { padding-left: 16px; padding-right: 16px; }
          #pricing .pricing-card { padding: 20px 18px; }
          #pricing .pricing-plan-title { gap: 10px; }
          #pricing .pricing-plan-title img { width: 52px; }
          #pricing .pricing-design-benefit { gap: 10px; }
          #pricing .pricing-design-benefit h4 { font-size: 20px; }
        }
        @media (prefers-reduced-motion: reduce) {
          #pricing .pricing-selector button, #pricing .pricing-download { transition: none; }
        }
      `}</style>
    </section>
  );
};

export default Pricing;
